/* eslint-disable react/no-unescaped-entities */
/**
 * Community Tutorial — deep dive into the forum: categories, posting,
 * reactions, ambassador, DMs, and moderation.
 */
import React from 'react';
import ToolSelector from '../../components/ToolSelector';
import TutorialLayout, { Callout, StepList, TryItButton } from '../../components/TutorialLayout';

const sections = [
  {
    id: 'overview',
    title: 'Welcome to the Community',
    body: (
      <>
        <p>
          The <strong>Community</strong> is where operators trade real advice — batch
          troubleshooting, gear reviews, cottage-food regulations, business war stories.
          It's built for signal, not scroll: threaded replies, clear categories,
          reactions instead of endless quote-replies, and an AI that helps you find
          answers already in the archive.
        </p>
        <p>
          The forum uses a <strong>three-column layout</strong>: a personal profile
          card on the left, a rich <strong>Feed</strong> in the middle, and Latest activity
          plus Events on the right. Every session starts on the Feed by default.
        </p>
        <TryItButton to="/community" label="Open the Community" />
      </>
    ),
  },
  {
    id: 'feed-first',
    title: 'The Feed — your home base',
    body: (
      <>
        <p>
          When you open <code>/community</code> you land on the <strong>Feed</strong> tab
          automatically. It shows posts from members you follow and connect with, plus
          public posts across the platform. Use the scope tabs (<em>For you</em>,
          <em> Following</em>, <em> Explore</em>) to change what you see.
        </p>
        <p>Every post supports:</p>
        <ul>
          <li><strong>Reactions</strong> — Like, Celebrate, Insightful, Funny.</li>
          <li><strong>Comments</strong> — threaded replies open lazily.</li>
          <li>
            <strong>Send</strong> — copies a shareable link so you can Direct Message it to a peer
            or paste it into a thread.
          </li>
          <li><strong>Image uploads</strong> and automatic link-previews in the composer.</li>
        </ul>
        <Callout kind="info">
          Sponsored cards appear intermittently every ~6 posts — they help fund the
          community. They're clearly labelled "Sponsored" and never mix with real posts.
        </Callout>
      </>
    ),
  },
  {
    id: 'profile',
    title: 'Your Member Profile',
    body: (
      <>
        <p>
          Your <strong>Member Profile</strong> at <code>/u/&lt;handle&gt;</code> is a
          full operator page. Click <em>Edit profile</em> to fill in:
        </p>
        <ul>
          <li><strong>Photo & banner</strong> — upload an avatar and a wide banner image.</li>
          <li><strong>Headline, About, Website, Handle</strong>.</li>
          <li>
            <strong>Operator stats</strong> — number of freeze dryers, years of
            experience, batches per month, tray capacity and business type. These
            surface as a dedicated card so peers know your context.
          </li>
          <li>
            <strong>Equipment</strong> — list every freeze dryer you own with brand,
            model, size, tray count, year acquired, condition and free-form notes.
            This powers "shared machines" suggestions and lets peers find you when
            they have questions about specific gear.
          </li>
          <li><strong>Skills</strong> — comma-separated. Other members can endorse them with a click.</li>
          <li><strong>Work history</strong>.</li>
          <li>
            <strong>Recommendations</strong> — peers can write a short note about you;
            you review and publish or hide it.
          </li>
        </ul>
        <p>
          On other members' profiles you'll see three new rails: <strong>Suggested for
          you</strong> (trending topics + recommended lessons), <strong>People also
          viewed</strong> (who visitors to their page also visited), and a promoted
          sponsored card. On your own profile you'll see <strong>Who viewed your
          profile</strong> — the members who visited in the last 30 days.
        </p>
      </>
    ),
  },
  {
    id: 'my-network',
    title: 'My Network — connections & invitations',
    body: (
      <>
        <p>
          The <strong>My Network</strong> page (<code>/network</code>, also linked in
          the top nav) is your invitation &amp; connections hub:
        </p>
        <ul>
          <li>
            <strong>Invitations</strong> — incoming requests waiting on your response
            plus outgoing invites you sent that are still pending.
          </li>
          <li><strong>Connections</strong> — everyone you've mutually accepted.</li>
          <li><strong>Followers &amp; Following</strong> — the classic one-way follow graph.</li>
          <li>
            <strong>Suggestions</strong> — <em>People you may know</em> powered by
            shared machines, shared courses, matching timezone and 2nd-degree
            connections (people your followers follow).
          </li>
        </ul>
        <p>
          To connect with someone, open their profile and click <strong>Connect</strong>.
          If they've already invited you, the click accepts instantly. Otherwise a
          request is queued and they'll see it on their Invitations tab.
        </p>
      </>
    ),
  },
  {
    id: 'dms',
    title: 'Direct Messages — the floating bubble',
    body: (
      <>
        <p>
          The chat bubble in the bottom-right of the Community is your always-on
          messages panel. Click it to see the 12 most-recent conversations with
          unread badges. Jump straight into a thread, or hit "Inbox" for the full
          <code> /messages</code> page and search.
        </p>
        <Callout kind="tip">
          To Direct Message a member, open their profile and hit the message icon.
        </Callout>
      </>
    ),
  },
  {
    id: 'theme',
    title: 'Personal theme',
    body: (
      <>
        <p>
          The <strong>Theme</strong> button (top-right of the Community header) lets
          you pick a Light or Dark appearance plus one of four accent colours:
          Amber, Blue, Emerald or Violet. Your choice is saved on this device and
          instantly re-skins the entire app.
        </p>
      </>
    ),
  },
  {
    id: 'layout',
    title: 'How the forum is organised',
    body: (
      <>
        <p>Everything hangs off three concepts:</p>
        <ul>
          <li>
            <strong>Categories</strong> — top-level rooms like <em>Getting Started</em>,
            <em> Equipment</em>, <em>Recipes</em>, <em>Business</em>, and <em>Bloopers</em>.
            Each has its own banner, description, rules and pinned resources.
          </li>
          <li>
            <strong>Topics</strong> — a discussion inside a category. Can be a regular
            question, an announcement, a poll, or an event.
          </li>
          <li>
            <strong>Replies</strong> — threaded one level deep so conversations stay easy
            to follow.
          </li>
        </ul>
        <Callout kind="info">
          The landing page pins a "Hot right now" hero, a "Just posted" strip (last
          24 h), and category cards, so brand-new questions always surface.
        </Callout>
      </>
    ),
  },
  {
    id: 'first-post',
    title: 'Writing your first topic',
    body: (
      <>
        <StepList
          steps={[
            'Click New topic in the top-right of any Community page — the composer opens as a dialog.',
            'Pick the category that best matches your question. Categories drive who sees it and the rules that apply.',
            'Write a clear title (10–80 characters is the sweet spot) and describe the situation in the body. Markdown works.',
            'Optional: drag & drop images (they upload to secure object storage), tag @members, and pick a "kind" — topic / poll / event / announcement.',
            'Hit Post. Your topic appears immediately in the category feed and pings anyone subscribed.',
          ]}
        />
        <Callout kind="tip">
          Post a photo when troubleshooting equipment. 90% of "why is my batch weird"
          questions get resolved in the first reply if there's an image of the tray.
        </Callout>
      </>
    ),
  },
  {
    id: 'reactions-hot',
    title: 'Reactions, hot-sorting & bookmarks',
    body: (
      <>
        <p>Reactions replace "+1" replies. Every topic and reply has five choices:</p>
        <p>
          <strong>👍 useful · ❤️ love · 🎉 celebrate · 🔥 fire · 🤔 hmm</strong>
        </p>
        <p>
          The "Hot right now" hero on <code>/community</code> ranks topics by a decay-weighted
          mix of views, replies, and reactions in the last 24 hours, so genuinely useful
          threads bubble up even without new replies.
        </p>
        <p>
          Bookmark any topic (the ribbon icon in the topic header) — bookmarked topics
          get their own tab in the sub-nav so you can jump straight back later.
        </p>
      </>
    ),
  },
  {
    id: 'archived-live',
    title: 'Live vs Archived tabs',
    body: (
      <>
        <p>
          Every category page has three tabs: <strong>Live</strong> (default) /
          <strong> Archived</strong> / <strong>All</strong>. Two things put a topic into
          Archived:
        </p>
        <ul>
          <li>An admin manually archives it (Archive button in the topic actions).</li>
          <li>
            The system auto-archives it after <strong>21 days with no activity</strong>. Pinned
            topics and announcements are never auto-archived.
          </li>
        </ul>
        <Callout kind="success">
          Anyone can hit <strong>Revive this topic</strong> to bring an archived thread
          back to Live — great for surfacing evergreen questions when someone rediscovers
          them.
        </Callout>
      </>
    ),
  },
  {
    id: 'ai',
    title: 'AI Summarize and Research + Fact Check with AI',
    body: (
      <>
        <p>
          Every forum page carries the AI. It runs on Claude Sonnet 4.5 and is trained
          on the same Freeze Dry Academy reference knowledge that guides our courses.
        </p>
        <h3>AI Summarize and Research</h3>
        <p>
          Collapsible panel on <code>/community</code> and every category page. Ask a
          natural-language question — the AI searches every active <em>and</em> archived
          post + reply, then answers in 1–3 sentences with links to the source threads.
          Zero copy-paste of long excerpts; you always land on the original conversation.
        </p>
        <h3>Fact Check with AI</h3>
        <p>
          Sidebar panel on every topic page. Runs a structured comparison of the whole
          thread against the reference knowledge base and returns:
        </p>
        <ul>
          <li>Overall verdict (accurate / partially accurate / misleading / unverified)</li>
          <li>Summary + key claims made in the thread</li>
          <li>Concerns (anything that contradicts best practice)</li>
          <li>Recommendations for readers</li>
        </ul>
        <Callout kind="warning">
          Posts that fail the fact check with high confidence get a public
          <strong> Under Review</strong> badge so every member sees the flag. Admins
          resolve them from the AI Moderation queue.
        </Callout>
      </>
    ),
  },
  {
    id: 'notifications',
    title: 'Notifications & the bell',
    body: (
      <>
        <p>The top-nav bell aggregates every community event that touches you:</p>
        <ul>
          <li>Replies to your topics</li>
          <li>Reactions on your posts</li>
          <li>@mentions</li>
          <li>Direct messages (DMs)</li>
          <li>AI moderation flags (admins only)</li>
        </ul>
        <p>
          Web push is enabled on subscribed devices so you keep getting pinged even
          when the tab is closed. Manage individual channels from
          <em> UserPortal → Activity → Notification settings</em>.
        </p>
      </>
    ),
  },
  {
    id: 'dms',
    title: 'Direct Messages',
    body: (
      <>
        <p>
          Every author card has a <strong>Message</strong> button (except your own).
          Click it to open a 1:1 conversation — the first message either opens an
          existing thread or starts a fresh one.
        </p>
        <StepList
          steps={[
            'Open any topic and scroll to the sidebar — every reply also has the author\'s profile card.',
            'Click Message to open the conversation (creates it if it\'s the first time).',
            'Type in the composer at the bottom. Shift-Enter for newline, Enter to send.',
            'Unread messages show a badge in the top nav, on your Dashboard, and in your Portal.',
          ]}
        />
        <TryItButton to="/messages" label="Open my inbox" />
      </>
    ),
  },
  {
    id: 'rules',
    title: 'Community rules & moderation',
    body: (
      <>
        <p>The forum stays useful because it's actively moderated. Non-negotiables:</p>
        <ul>
          <li>Be helpful and respectful. Attack the idea, not the person.</li>
          <li>No spam, no self-promotion outside the vendor category.</li>
          <li>
            Never share personal information about other members. Screenshots of DMs
            without consent are grounds for suspension.
          </li>
          <li>
            No dangerous food-safety advice. Posts that clearly contradict best practice
            can be flagged by the AI and hidden pending review.
          </li>
        </ul>
        <p>
          Admins can pin, lock, archive, delete, or reassign category. Repeat offenders
          are suspended or banned from the User Management panel.
        </p>
      </>
    ),
  },
  {
    id: 'pro-tips',
    title: 'Pro tips from the team',
    body: (
      <>
        <ul>
          <li>Search first — most questions have been asked. The AI Summarize panel is faster than scrolling.</li>
          <li>
            Use polls for "which should I buy" style questions. You'll get more responses
            than an open-ended thread.
          </li>
          <li>
            Post events (kind = event) when hosting a virtual meetup — they land on the
            Events tab and send a reminder to interested members.
          </li>
          <li>Reactions are free — use them liberally to reward great answers. It helps hot-sorting.</li>
        </ul>
      </>
    ),
  },
];

export default function TutorialCommunity() {
  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <TutorialLayout
        eyebrow="Tutorial"
        title="Community forum walkthrough"
        subtitle="A full-length guide to the Freeze Dry Academy community — how to post, react, use the AI, message members, and stay on the right side of the rules."
        sections={sections}
        backTo="/community"
        backLabel="Back to Community"
      />
    </div>
  );
}
